// Vendored from the JETCAM DXF Viewer (src/geometry/extents.ts), 2026-09-02.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Local edits are marked "dxfsplitter:". Do not reformat; keep diffable against origin.

import { type Vec3 } from "../parse/index.ts";
import { type ArcPrimitive, type Bounds, type Primitive } from "./types.ts";

const FULL_TURN = Math.PI * 2;
const ANGLE_EPSILON = 1e-9;

/** Positive remainder, so an angle difference always lands in [0, 2*PI). */
function mod2pi(a: number): number {
  const r = a % FULL_TURN;
  return r < 0 ? r + FULL_TURN : r;
}

/** How far the arc sweeps, always positive, up to a full turn. */
export function sweepOf(arc: ArcPrimitive): number {
  const raw = arc.ccw ? arc.endAngle - arc.startAngle : arc.startAngle - arc.endAngle;
  if (Math.abs(raw) >= FULL_TURN - ANGLE_EPSILON) return FULL_TURN;
  let sweep = raw;
  while (sweep <= 0) sweep += FULL_TURN;
  return sweep;
}

/**
 * Whether the arc actually passes through the given world angle.
 *
 * Exported because the hit test needs exactly the same judgement: a click near
 * a circle is only a hit if the arc is actually drawn at that bearing. Two
 * copies of this would drift.
 */
export function arcCoversAngle(arc: ArcPrimitive, angle: number): boolean {
  const sweep = sweepOf(arc);
  if (sweep >= FULL_TURN - ANGLE_EPSILON) return true;
  const travelled = arc.ccw ? mod2pi(angle - arc.startAngle) : mod2pi(arc.startAngle - angle);
  return travelled <= sweep + ANGLE_EPSILON;
}

class BoxAccumulator {
  minX = Infinity;
  minY = Infinity;
  minZ = Infinity;
  maxX = -Infinity;
  maxY = -Infinity;
  maxZ = -Infinity;
  empty = true;

  add(p: Vec3): void {
    this.empty = false;
    if (p.x < this.minX) this.minX = p.x;
    if (p.y < this.minY) this.minY = p.y;
    if (p.z < this.minZ) this.minZ = p.z;
    if (p.x > this.maxX) this.maxX = p.x;
    if (p.y > this.maxY) this.maxY = p.y;
    if (p.z > this.maxZ) this.maxZ = p.z;
  }

  result(): Bounds | null {
    if (this.empty) return null;
    return {
      min: { x: this.minX, y: this.minY, z: this.minZ },
      max: { x: this.maxX, y: this.maxY, z: this.maxZ },
    };
  }
}

/**
 * Bounding box of a display list, in world coordinates.
 *
 * Arcs are handled properly rather than by their endpoints: a quarter arc
 * spanning due north reaches further up than either end does, and using the
 * endpoints alone would crop it on zoom-to-extents. The four cardinal points
 * are the only places an axis-aligned box can touch a circle, so it is enough
 * to test which of them the sweep actually covers.
 */
export function boundsOf(primitives: readonly Primitive[]): Bounds | null {
  const box = new BoxAccumulator();

  for (const p of primitives) {
    switch (p.kind) {
      case "line":
        box.add(p.a);
        box.add(p.b);
        break;
      case "point":
        box.add(p.position);
        break;
      case "polyline":
        for (const pt of p.points) box.add(pt);
        break;
      case "arc": {
        box.add({
          x: p.center.x + p.radius * Math.cos(p.startAngle),
          y: p.center.y + p.radius * Math.sin(p.startAngle),
          z: p.center.z,
        });
        box.add({
          x: p.center.x + p.radius * Math.cos(p.endAngle),
          y: p.center.y + p.radius * Math.sin(p.endAngle),
          z: p.center.z,
        });
        for (let q = 0; q < 4; q += 1) {
          const angle = (q * Math.PI) / 2;
          if (!arcCoversAngle(p, angle)) continue;
          box.add({
            x: p.center.x + p.radius * Math.cos(angle),
            y: p.center.y + p.radius * Math.sin(angle),
            z: p.center.z,
          });
        }
        break;
      }
      case "text":
        // Only the insertion point. Text width needs font metrics, which this
        // layer has no business knowing, and guessing at it would inflate the
        // extents of a part whose annotation sits outside the profile.
        box.add(p.position);
        break;
    }
  }

  return box.result();
}
